/* ═══════════════════════════════════════════════════════════════════════════
   BAR SERVICE — IMAGE DATA
   Premium visual experience for banquet booking (mirrors foodServiceData.ts)
   ═══════════════════════════════════════════════════════════════════════════ */

export type MediaType = "image" | "video";

export interface ServiceGalleryItem {
  image: string;
  title: string;
  tag?: "Popular" | "Luxury" | "Standard" | "Premium" | "Recommended";
  mediaType?: MediaType; // explicit: "image" (default) or "video"
  video?: string;        // MP4 URL — only used when mediaType === "video"
}

/* ── Bar Service Hero Images ── */
export interface BarServiceHero {
  image: string;
  description: string;
  badge?: string;
  mediaType?: MediaType;
  video?: string;
}

export const BAR_SERVICE_IMAGES: Record<string, BarServiceHero> = {
  "open bar": {
    image: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=900&h=500&fit=crop",
    description: "Unlimited drinks with professional bartenders all evening",
    badge: "Popular",
    mediaType: "video",
    video: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4",
  },
  "cash bar": {
    image: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=900&h=500&fit=crop",
    description: "Pay-per-drink bar with full cocktail and beverage menu",
  },
  "premium": {
    image: "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=900&h=500&fit=crop",
    description: "Top-shelf spirits, craft cocktails, and premium wine selection",
    badge: "Luxury",
    mediaType: "video",
    video: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4",
  },
  "standard": {
    image: "https://images.unsplash.com/photo-1543007630-9710e4a00a20?w=900&h=500&fit=crop",
    description: "House spirits, beer, and wine with professional service",
  },
  "cocktail": {
    image: "https://images.unsplash.com/photo-1536935338788-846bb9981813?w=900&h=500&fit=crop",
    description: "Signature cocktail service with custom drink menu",
    badge: "Recommended",
  },
  "wine": {
    image: "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=900&h=500&fit=crop",
    description: "Curated wine selection paired with your dining experience",
  },
  "mocktail": {
    image: "https://images.unsplash.com/photo-1544145945-f90425340c7e?w=900&h=500&fit=crop",
    description: "Non-alcoholic craft beverages and artisan mocktails",
  },
};

const DEFAULT_BAR_IMG = {
  image: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=900&h=500&fit=crop",
  description: "Professional bar service for your event",
};

export const getBarServiceData = (name: string) => {
  if (!name) return null;
  const lower = name.toLowerCase();
  for (const [key, val] of Object.entries(BAR_SERVICE_IMAGES)) {
    if (lower.includes(key)) return val;
  }
  return { ...DEFAULT_BAR_IMG, description: `${name} — curated beverage service for your event` };
};

/* ── Bar Service Gallery Items ── */
export const BAR_SERVICE_GALLERY: Record<string, ServiceGalleryItem[]> = {
  "open bar": [
    { image: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=700&h=500&fit=crop", title: "Classic Open Bar Setup", tag: "Popular" },
    { image: "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=700&h=500&fit=crop", title: "Premium Open Bar", tag: "Luxury", mediaType: "video", video: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4" },
    { image: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=700&h=500&fit=crop", title: "Wedding Open Bar", tag: "Recommended" },
    { image: "https://images.unsplash.com/photo-1543007630-9710e4a00a20?w=700&h=500&fit=crop", title: "Corporate Open Bar", tag: "Standard" },
    { image: "https://images.unsplash.com/photo-1536935338788-846bb9981813?w=700&h=500&fit=crop", title: "Cocktail Hour Setup" },
  ],
  "cash bar": [
    { image: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=700&h=500&fit=crop", title: "Cash Bar Station", tag: "Popular" },
    { image: "https://images.unsplash.com/photo-1543007630-9710e4a00a20?w=700&h=500&fit=crop", title: "Event Cash Bar", tag: "Standard" },
    { image: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=700&h=500&fit=crop", title: "Modern Cash Bar", tag: "Premium" },
    { image: "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=700&h=500&fit=crop", title: "Lounge Cash Bar" },
  ],
  "premium": [
    { image: "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=700&h=500&fit=crop", title: "Top-Shelf Bar", tag: "Luxury", mediaType: "video", video: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4" },
    { image: "https://images.unsplash.com/photo-1536935338788-846bb9981813?w=700&h=500&fit=crop", title: "Craft Cocktail Station", tag: "Premium" },
    { image: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=700&h=500&fit=crop", title: "Premium Wine & Spirits", tag: "Popular" },
    { image: "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=700&h=500&fit=crop", title: "Wine Tasting Setup", tag: "Recommended" },
    { image: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=700&h=500&fit=crop", title: "Luxury Mixology Station" },
  ],
  "cocktail": [
    { image: "https://images.unsplash.com/photo-1536935338788-846bb9981813?w=700&h=500&fit=crop", title: "Signature Cocktail Bar", tag: "Popular" },
    { image: "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=700&h=500&fit=crop", title: "Custom Cocktail Menu", tag: "Luxury" },
    { image: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=700&h=500&fit=crop", title: "Mixology Experience", tag: "Premium" },
    { image: "https://images.unsplash.com/photo-1544145945-f90425340c7e?w=700&h=500&fit=crop", title: "Tropical Cocktail Setup", tag: "Recommended" },
  ],
};

export const getBarServiceGallery = (name: string): ServiceGalleryItem[] => {
  if (!name) return [];
  const lower = name.toLowerCase();
  for (const [key, items] of Object.entries(BAR_SERVICE_GALLERY)) {
    if (lower.includes(key)) return items;
  }
  return BAR_SERVICE_GALLERY["open bar"] || [];
};

/* ── Bar Service Dropdown Thumbnails ── */
export const BAR_SERVICE_THUMBS: Record<string, string> = {
  "open bar": "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=80&h=60&fit=crop",
  "cash bar": "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=80&h=60&fit=crop",
  "premium": "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=80&h=60&fit=crop",
  "standard": "https://images.unsplash.com/photo-1543007630-9710e4a00a20?w=80&h=60&fit=crop",
  "cocktail": "https://images.unsplash.com/photo-1536935338788-846bb9981813?w=80&h=60&fit=crop",
  "wine": "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=80&h=60&fit=crop",
  "mocktail": "https://images.unsplash.com/photo-1544145945-f90425340c7e?w=80&h=60&fit=crop",
};

export const getBarServiceThumb = (name: string): string | null => {
  if (!name) return null;
  const lower = name.toLowerCase();
  for (const [key, url] of Object.entries(BAR_SERVICE_THUMBS)) {
    if (lower.includes(key)) return url;
  }
  return "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=80&h=60&fit=crop";
};
